import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, Chip, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Divider, Tabs, Tab, Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PayrollService } from '../../services/payroll.service';
import { IPayroll, IPayrollSettings } from '../../interfaces/payroll.interface';
import { IPayrollPaymentStatus, ISalaryPayment } from '../../interfaces/salaryPayment';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { displayUsername } from '../../utils/userUtils';
import { formatMoney, payslipFileBase } from '../../utils/format';
import PaymentStatusChip from './PaymentStatusChip';
import PayrollAccrualChip from './PayrollAccrualChip';
import PaymentFormDialog from './PaymentFormDialog';
import PaymentReversalDialog from './PaymentReversalDialog';
import SkipJournalDialog from './SkipJournalDialog';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const formatOvertime = (minutes: number) =>
    `${Math.floor(minutes / 60)}h ${minutes % 60}m`;

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index}>
        {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
);

const PayrollDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [payroll, setPayroll] = useState<IPayroll | null>(null);
    const [tab, setTab] = useState(0);
    const [paymentStatus, setPaymentStatus] = useState<IPayrollPaymentStatus | null>(null);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [reverseTargetId, setReverseTargetId] = useState<string | null>(null);
    const [skipDialogOpen, setSkipDialogOpen] = useState(false);
    const [skipPaymentId, setSkipPaymentId] = useState<string | null>(null);
    const [emailing, setEmailing] = useState(false);
    // Bangla payslip (2026-09-11): the institution's payslipLanguage setting decides which
    // secondary "other language" button to show next to Download Payslip.
    const [payslipLanguage, setPayslipLanguage] = useState<'EN' | 'BN'>('EN');
    const { showSuccess, showError, showApiError } = useToast();

    const canCreatePayment = usePermission(PERMISSIONS.SALARY_PAYMENT_MANAGE);
    const canReversePayment = usePermission(PERMISSIONS.SALARY_PAYMENT_MANAGE);
    const canOverride = usePermission(PERMISSIONS.SALARY_PAYMENT_MANAGE);
    const canManagePayroll = usePermission(PERMISSIONS.PAYROLL_MANAGE);
    const [retryingAccrual, setRetryingAccrual] = useState(false);

    const loadPayroll = useCallback(async () => {
        try {
            const data = await PayrollService.getPayrollById(id!);
            setPayroll(data);
        } catch (error) {
            showApiError(error, 'Failed to load payroll details');
        }
    }, [id, showApiError]);

    const loadPaymentStatus = useCallback(async () => {
        if (!id) return;
        try {
            const data = await PayrollService.getPayrollPaymentStatus(id);
            setPaymentStatus(data);
        } catch (error) {
            showApiError(error, 'Failed to load payment status');
        }
    }, [id, showApiError]);

    const loadPayslipLanguage = useCallback(async (institutionId: string) => {
        try {
            const data: IPayrollSettings | null = await PayrollService.getPayrollSettings(institutionId);
            setPayslipLanguage(data?.payslipLanguage ?? 'EN');
        } catch (error) {
            // Best-effort only: fall back to EN (today's default) rather than blocking the
            // Download Payslip button on a broken settings fetch.
            setPayslipLanguage('EN');
        }
    }, []);

    useEffect(() => {
        if (id) loadPayroll();
    }, [id, loadPayroll]);

    useEffect(() => {
        if (tab === 1 && id) loadPaymentStatus();
    }, [tab, id, loadPaymentStatus]);

    useEffect(() => {
        if (payroll?.institutionId) loadPayslipLanguage(payroll.institutionId);
    }, [payroll?.institutionId, loadPayslipLanguage]);

    const handleDownload = async () => {
        try {
            await PayrollService.downloadPayslip(id!, undefined, payroll ? payslipFileBase(payroll) : undefined);
        } catch (error) {
            showApiError(error, 'Failed to download payslip');
        }
    };

    const handleDownloadOtherLanguage = async () => {
        try {
            await PayrollService.downloadPayslip(id!, payslipLanguage === 'EN' ? 'BN' : 'EN', payroll ? payslipFileBase(payroll) : undefined);
        } catch (error) {
            showApiError(error, 'Failed to download payslip');
        }
    };

    const handleEmail = async () => {
        setEmailing(true);
        try {
            await PayrollService.emailPayslip(id!);
            showSuccess('Payslip emailed');
            setPayroll(prev => prev ? { ...prev, payslipEmailedAt: new Date().toISOString() } : prev);
        } catch (error) {
            showApiError(error, 'Failed to email payslip');
        } finally {
            setEmailing(false);
        }
    };

    const handlePaymentSuccess = () => {
        loadPaymentStatus();
    };

    const handleRetryPost = async (paymentId: string) => {
        try {
            const result = await PayrollService.retryJournalPost(paymentId);
            if (result?.journalPostStatus && result.journalPostStatus !== 'POSTED') {
                // The API answers 200 even when the entry could not be posted (missing accounts etc.).
                showError(result.journalPostWarning || result.journalPostError
                    || 'The journal entry could not be posted. Check the account settings in Payroll Settings.');
            } else {
                showSuccess('Journal post retry succeeded');
            }
            loadPaymentStatus();
        } catch (error) {
            showApiError(error, 'Retry failed');
        }
    };

    const handleRetryAccrual = async () => {
        if (!id || retryingAccrual) return;
        setRetryingAccrual(true);
        try {
            const updated = await PayrollService.retryAccrual(id);
            setPayroll(updated);
            if (updated.accrualStatus === 'POSTED') {
                showSuccess('Payroll accrual posted');
            } else {
                showError(updated.accrualError || 'Accrual could not be posted');
            }
        } catch (error) {
            showApiError(error, 'Failed to retry accrual');
        } finally {
            setRetryingAccrual(false);
        }
    };

    const handleOpenSkip = (paymentId: string) => {
        setSkipPaymentId(paymentId);
        setSkipDialogOpen(true);
    };

    const handleCloseSkip = () => {
        setSkipDialogOpen(false);
        setSkipPaymentId(null);
    };

    if (!payroll) return <Box sx={{ p: 3 }}><Typography>Loading...</Typography></Box>;

    const totalAllDeductions = payroll.totalDeductions + payroll.unpaidLeaveDeduction + payroll.absenceDeduction;
    const isFinalized = payroll.status === 'FINALIZED';

    return (
        <Box data-help-section="payroll.detail" data-help-page="payroll.detail.page" sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Button data-help="payroll.detail.back" startIcon={<ArrowBackIcon />} onClick={() => navigate('/payroll/run')}>
                    Back
                </Button>
                <Typography variant="h5">Payroll Detail</Typography>
                <Chip label={payroll.status} color={isFinalized ? 'success' : 'warning'} />
                <PayrollAccrualChip payroll={payroll} />
                {payroll.accrualStatus === 'NOT_POSTED' && canManagePayroll && (
                    <Button data-help="payroll.detail.retryAccrual" size="small" variant="outlined" color="warning" onClick={handleRetryAccrual}
                        disabled={retryingAccrual}>
                        Retry accrual
                    </Button>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ width: { xs: '100%', md: 'auto' } }}>
                    {payroll.payslipEmailedAt && (
                        <Chip
                            icon={<CheckCircleIcon />}
                            color="success"
                            variant="outlined"
                            size="small"
                            label="Emailed"
                        />
                    )}
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        data-help="payroll.detail.downloadPayslip"
                        onClick={handleDownload}
                        disabled={!isFinalized}
                        sx={{ flex: { xs: '1 1 150px', md: '0 0 auto' } }}
                    >
                        Download Payslip
                    </Button>
                    <Button
                        data-help="payroll.detail.otherLanguage"
                        variant="outlined"
                        size="small"
                        onClick={handleDownloadOtherLanguage}
                        disabled={!isFinalized}
                    >
                        {payslipLanguage === 'EN' ? 'বাংলা' : 'English'}
                    </Button>
                    <Button
                        data-help="payroll.detail.emailPayslip"
                        variant="outlined"
                        startIcon={<EmailIcon />}
                        onClick={handleEmail}
                        disabled={!isFinalized || emailing}
                        sx={{ flex: { xs: '1 1 150px', md: '0 0 auto' } }}
                    >
                        {payroll.payslipEmailedAt ? 'Resend Payslip' : 'Email Payslip'}
                    </Button>
                </Stack>
            </Box>

            <Paper sx={{ mb: 3 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                    <Tab label="Details" />
                    <Tab label="Payments" />
                </Tabs>
            </Paper>

            <TabPanel value={tab} index={0}>
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {payroll.employeeName || displayUsername(payroll.userName)} - {MONTHS[payroll.month - 1]} {payroll.year}
                    </Typography>
                    {payroll.payableDays != null && payroll.periodDays != null && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Payable days {payroll.payableDays} / {payroll.periodDays} (salary prorated for a part-month)
                        </Typography>
                    )}

                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Earnings</Typography>
                    <TableContainer data-help-section="payroll.detail.earnings">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="payroll.detail.col.component">Component</TableCell>
                                    <TableCell align="right" data-help="payroll.detail.col.amount">Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {payroll.earningsBreakdown && Object.entries(payroll.earningsBreakdown).map(([name, amount]) => (
                                    <TableRow key={name}>
                                        <TableCell>{name}</TableCell>
                                        <TableCell align="right">{formatMoney(amount)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell data-help="payroll.detail.totalGross" sx={{ fontWeight: 'bold' }}>Total Earnings (Gross)</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatMoney(payroll.grossSalary)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Informational only — the overtime amount is already included in one of the
                        earningsBreakdown rows above (and in Gross) via the designated overtime
                        earning component, or the "Overtime Pay" fallback key. This line must not
                        be added to the earnings sum; it only annotates the hours behind that figure. */}
                    {!!payroll.overtimeMinutes && payroll.overtimeMinutes > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Overtime: {formatOvertime(payroll.overtimeMinutes)} — {formatMoney(payroll.overtimeAmount ?? 0)}
                        </Typography>
                    )}

                    {/* Informational only, same as the overtime line above — the amount is already
                        included in one of the earningsBreakdown rows (and in Gross) via the D10
                        designated bonus-earning component, or the fallback "Festival Bonus (...)"
                        / "Attendance Bonus" keys (spec §2.2/§2.3). Not added to the earnings sum. */}
                    {!!payroll.festivalBonusAmount && payroll.festivalBonusAmount > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Festival Bonus: {formatMoney(payroll.festivalBonusAmount)}
                        </Typography>
                    )}

                    {!!payroll.attendanceBonusAmount && payroll.attendanceBonusAmount > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Attendance Bonus: {formatMoney(payroll.attendanceBonusAmount)}
                        </Typography>
                    )}

                    {/* Informational only, same pattern as the festival/attendance bonus lines
                        above — the amount is already included in one of the earningsBreakdown
                        rows (and in Gross) via the "Leave Encashment (<Type Name>)" key (spec
                        §2.2 step 3). Not added to the earnings sum. */}
                    {!!payroll.leaveEncashmentAmount && payroll.leaveEncashmentAmount > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Leave Encashment: {formatMoney(payroll.leaveEncashmentAmount)}
                        </Typography>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Deductions</Typography>
                    <TableContainer data-help-section="payroll.detail.deductions">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="payroll.detail.col.component">Component</TableCell>
                                    <TableCell align="right" data-help="payroll.detail.col.amount">Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {payroll.deductionsBreakdown && Object.entries(payroll.deductionsBreakdown).map(([name, amount]) => (
                                    <TableRow key={name}>
                                        <TableCell>{name}</TableCell>
                                        <TableCell align="right">{formatMoney(amount)}</TableCell>
                                    </TableRow>
                                ))}
                                {payroll.unpaidLeaveDays > 0 && (
                                    <TableRow>
                                        <TableCell>Unpaid Leave ({payroll.unpaidLeaveDays} days)</TableCell>
                                        <TableCell align="right">{formatMoney(payroll.unpaidLeaveDeduction)}</TableCell>
                                    </TableRow>
                                )}
                                {payroll.absentDays > 0 && (
                                    <TableRow>
                                        <TableCell>Absence ({payroll.absentDays} days{Number(payroll.absenceDeduction) === 0 ? ', not deducted' : ''})</TableCell>
                                        <TableCell align="right">{formatMoney(payroll.absenceDeduction)}</TableCell>
                                    </TableRow>
                                )}
                                <TableRow>
                                    <TableCell data-help="payroll.detail.totalDeductions" sx={{ fontWeight: 'bold' }}>Total Deductions</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatMoney(totalAllDeductions)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="h6" data-help="payroll.detail.netSalary">Net Salary</Typography>
                        <Typography variant="h6" color="primary">{formatMoney(payroll.netSalary)}</Typography>
                    </Box>
                </Paper>
            </TabPanel>

            <TabPanel value={tab} index={1}>
                {!paymentStatus ? (
                    <Typography>Loading payment status...</Typography>
                ) : (
                    <>
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" data-help="payroll.detail.payNet">Net Salary</Typography>
                                    <Typography variant="h6">{formatMoney(paymentStatus.netSalary)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" data-help="payroll.detail.totalPaid">Total Paid</Typography>
                                    <Typography variant="h6">{formatMoney(paymentStatus.totalPaid)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" data-help="payroll.detail.outstanding">Outstanding</Typography>
                                    <Typography variant="h6" color={paymentStatus.outstanding > 0 ? 'warning.main' : 'text.primary'}>
                                        {formatMoney(paymentStatus.outstanding)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Chip
                                        label={paymentStatus.fullyPaid ? 'Fully Paid' : 'Not Fully Paid'}
                                        color={paymentStatus.fullyPaid ? 'success' : 'warning'}
                                    />
                                </Box>
                                <Box sx={{ flexGrow: 1 }} />
                                {canCreatePayment && isFinalized && (
                                    <Button
                                        variant="contained"
                                        data-help="payroll.detail.recordPayment"
                                        onClick={() => setPaymentDialogOpen(true)}
                                    >
                                        Record Payment
                                    </Button>
                                )}
                            </Stack>
                        </Paper>

                        <Paper sx={{ p: 3 }}>
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Payment History</Typography>
                            <TableContainer data-help-section="payroll.detail.payments">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell data-help="payroll.detail.payCol.date">Date</TableCell>
                                            <TableCell align="right" data-help="payroll.detail.payCol.amount">Amount</TableCell>
                                            <TableCell data-help="payroll.detail.payCol.method">Method</TableCell>
                                            <TableCell data-help="payroll.detail.payCol.status">Status</TableCell>
                                            <TableCell data-help="payroll.detail.payCol.reason">Reason</TableCell>
                                            <TableCell align="right" data-help="payroll.detail.payCol.actions">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paymentStatus.payments.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center">
                                                    <Typography variant="body2" color="text.secondary">
                                                        No payments recorded yet.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {paymentStatus.payments.map((p: ISalaryPayment) => {
                                            const canReverse = canReversePayment
                                                && p.status === 'COMPLETED'
                                                && !p.reversesPaymentId;
                                            const needsJournalAction = p.journalPostStatus === 'FAILED' || p.journalPostStatus === 'SKIPPED';
                                            return (
                                                <TableRow key={p.id}>
                                                    <TableCell>{p.paymentDate}</TableCell>
                                                    <TableCell align="right">{formatMoney(p.amount)}</TableCell>
                                                    <TableCell>{p.paymentMethod}</TableCell>
                                                    <TableCell>
                                                        <PaymentStatusChip
                                                            status={p.status}
                                                            journalPostStatus={p.journalPostStatus}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{p.reason || '-'}</TableCell>
                                                    <TableCell align="right">
                                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                            {canReverse && (
                                                                <Button
                                                                    data-help="payroll.detail.action.reverse"
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => setReverseTargetId(p.id)}
                                                                >
                                                                    Reverse
                                                                </Button>
                                                            )}
                                                            {needsJournalAction && canCreatePayment && (
                                                                <Button
                                                                    data-help="payroll.detail.action.retryPost"
                                                                    size="small"
                                                                    onClick={() => handleRetryPost(p.id)}
                                                                >
                                                                    Retry Post
                                                                </Button>
                                                            )}
                                                            {needsJournalAction && canOverride && (
                                                                <Button
                                                                    data-help="payroll.detail.action.skipPost"
                                                                    size="small"
                                                                    color="warning"
                                                                    onClick={() => handleOpenSkip(p.id)}
                                                                >
                                                                    Skip
                                                                </Button>
                                                            )}
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </>
                )}
            </TabPanel>

            {paymentStatus && (
                <PaymentFormDialog
                    open={paymentDialogOpen}
                    onClose={() => setPaymentDialogOpen(false)}
                    payrollId={id!}
                    netSalary={paymentStatus.netSalary}
                    outstanding={paymentStatus.outstanding}
                    onSuccess={handlePaymentSuccess}
                />
            )}

            <PaymentReversalDialog
                open={reverseTargetId !== null}
                onClose={() => setReverseTargetId(null)}
                paymentId={reverseTargetId || ''}
                onSuccess={handlePaymentSuccess}
            />

            <SkipJournalDialog
                open={skipDialogOpen}
                onClose={handleCloseSkip}
                paymentId={skipPaymentId || ''}
                onSuccess={handlePaymentSuccess}
            />
        </Box>
    );
};

export default PayrollDetail;
